// sentry.js - Sentry 헬퍼 함수
// @sentry/svelte SDK 사용 (main.js에서 초기화됨)

import * as Sentry from '@sentry/svelte';

/**
 * 사용자 컨텍스트 설정 (세션 연결 시)
 */
export function setSentryUser(sessionId, deviceName) {
  Sentry.setUser({
    id: sessionId ? `sess_${sessionId.slice(0, 8)}` : undefined,
  });
  Sentry.setTag('device.name', deviceName || 'unknown');
}

/**
 * 사용자 컨텍스트 초기화 (세션 종료 시)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * 수동 에러 리포트
 */
export function captureError(error, context = {}) {
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

/**
 * 수동 메시지 리포트
 */
export function captureMessage(message, level = 'info', context = {}) {
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureMessage(message, level);
  });
}

/**
 * 화면 전환 추적
 */
export function trackScreenChange(screenName) {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Screen: ${screenName}`,
    level: 'info',
  });
}

/**
 * Sentry trace 헤더 가져오기 (distributed tracing용)
 * @returns {{ 'sentry-trace'?: string, baggage?: string }}
 */
export function getSentryTraceHeaders() {
  const span = Sentry.getActiveSpan();
  if (!span) {
    return {};
  }

  const headers = {};
  const traceHeader = Sentry.spanToTraceHeader(span);
  if (traceHeader) {
    headers['sentry-trace'] = traceHeader;
  }

  const baggageHeader = Sentry.spanToBaggageHeader(span);
  if (baggageHeader) {
    headers['baggage'] = baggageHeader;
  }

  return headers;
}

/**
 * 성능 추적용 span 생성
 * @param {string} op - 작업 타입 (e.g., 'http.client', 'ui.action')
 * @param {string} name - 작업 이름
 * @param {Function} fn - 실행할 함수
 * @param {Object} attributes - 추가 속성
 */
export async function withSpan(op, name, fn, attributes = {}) {
  return Sentry.startSpan(
    { op, name, attributes },
    async () => {
      return fn();
    }
  );
}

/**
 * Breadcrumb 추가
 */
export function addBreadcrumb({ message, category, data, level = 'info' }) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level,
  });
}

/**
 * 태그 설정
 */
export function setTag(key, value) {
  Sentry.setTag(key, value);
}

/**
 * Extra 데이터 설정
 */
export function setExtra(key, value) {
  Sentry.setExtra(key, value);
}
